/**
 * Local account + vault identity.
 *
 * One encryption key (salt) maps to one account. Account id is derived from the
 * salt so the same key on another device yields the same account without a backend.
 * Default vault id equals account id. Multiple vaults per account are supported
 * in the model; only the active vault blob is stored in encryptedPasswords today.
 */

export const ACCOUNT_STATUS = {
    UNCONFIRMED: 'unconfirmed',
    CONFIRMED: 'confirmed',
};

export const IDENTITY_STORAGE_KEY = 'selfpass_identity';
export const DEFAULT_VAULT_NAME = 'Основной сейф';

const encodeText = (value) => new TextEncoder().encode(value);

const bytesToHex = (bytes) =>
    Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

/** Stable account id for a salt — same key ⇒ same account everywhere. */
export const deriveAccountId = async (salt) => {
    if (!salt || typeof salt !== 'string') {
        throw new Error('Ключ не задан');
    }

    const digest = await crypto.subtle.digest(
        'SHA-256',
        encodeText(`selfpass:account:${salt}`)
    );
    return bytesToHex(new Uint8Array(digest)).slice(0, 32);
};

export const createDefaultVault = (accountId, createdAt = new Date().toISOString()) => ({
    id: accountId,
    accountId,
    name: DEFAULT_VAULT_NAME,
    isDefault: true,
    createdAt,
});

export const createUnconfirmedAccount = (accountId, createdAt = new Date().toISOString()) => ({
    id: accountId,
    status: ACCOUNT_STATUS.UNCONFIRMED,
    createdAt,
    email: null,
    confirmedAt: null,
});

export const createIdentity = (accountId, createdAt = new Date().toISOString()) => {
    const account = createUnconfirmedAccount(accountId, createdAt);
    const vault = createDefaultVault(accountId, createdAt);
    return {
        account,
        vaults: [vault],
        activeVaultId: vault.id,
    };
};

export const readIdentity = () => {
    try {
        if (typeof localStorage === 'undefined') return null;
        const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.account?.id || !Array.isArray(parsed.vaults)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const writeIdentity = (identity) => {
    if (!identity?.account?.id) {
        throw new Error('Некорректная идентичность');
    }
    if (typeof localStorage === 'undefined') {
        return identity;
    }
    localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
    return identity;
};

export const clearIdentity = () => {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(IDENTITY_STORAGE_KEY);
};

export const getActiveVault = (identity = readIdentity()) => {
    if (!identity?.vaults?.length) return null;
    const activeId = identity.activeVaultId;
    return (
        identity.vaults.find((vault) => vault.id === activeId)
        || identity.vaults.find((vault) => vault.isDefault)
        || identity.vaults[0]
        || null
    );
};

/** Default vault is always linked by account id (even before multi-vault UI). */
export const resolveAccountVaultIds = async (salt) => {
    const accountId = await deriveAccountId(salt);
    const identity = typeof localStorage !== 'undefined' ? readIdentity() : null;
    const active = identity?.account?.id === accountId ? getActiveVault(identity) : null;

    return {
        accountId,
        vaultId: active?.id || accountId,
        accountStatus: identity?.account?.status || ACCOUNT_STATUS.UNCONFIRMED,
        vaultName: active?.name || DEFAULT_VAULT_NAME,
    };
};

/**
 * Ensure local identity exists for the given salt.
 * Creates an unconfirmed account + default vault when missing or mismatched.
 */
export const ensureIdentityForSalt = async (salt) => {
    const accountId = await deriveAccountId(salt);
    const existing = readIdentity();

    if (
        existing?.account?.id === accountId
        && existing.vaults.some((vault) => vault.accountId === accountId)
    ) {
        const hasDefault = existing.vaults.some(
            (vault) => vault.isDefault || vault.id === accountId
        );
        if (!hasDefault) {
            const next = {
                ...existing,
                vaults: [createDefaultVault(accountId), ...existing.vaults],
                activeVaultId: existing.activeVaultId || accountId,
            };
            return writeIdentity(next);
        }
        if (!existing.activeVaultId) {
            return writeIdentity({...existing, activeVaultId: accountId});
        }
        return existing;
    }

    return writeIdentity(createIdentity(accountId));
};

/**
 * Attach registration profile to the current key-bound account.
 * Kept for the future registration flow — works for already unconfirmed accounts.
 */
export const confirmAccount = ({email} = {}) => {
    const identity = readIdentity();
    if (!identity) {
        throw new Error('Аккаунт не найден');
    }

    const confirmedAt = new Date().toISOString();
    const next = {
        ...identity,
        account: {
            ...identity.account,
            status: ACCOUNT_STATUS.CONFIRMED,
            email: email || identity.account.email || null,
            confirmedAt,
        },
    };
    return writeIdentity(next);
};

/** Add another vault under the current account (same encryption key). */
export const addVault = (name = 'Новый сейф') => {
    const identity = readIdentity();
    if (!identity?.account?.id) {
        throw new Error('Аккаунт не найден');
    }

    const createdAt = new Date().toISOString();
    const vaultId = `${identity.account.id.slice(0, 16)}${bytesToHex(
        crypto.getRandomValues(new Uint8Array(8))
    )}`;
    const vault = {
        id: vaultId,
        accountId: identity.account.id,
        name: String(name || 'Новый сейф').trim() || 'Новый сейф',
        isDefault: false,
        createdAt,
    };

    return writeIdentity({
        ...identity,
        vaults: [...identity.vaults, vault],
    });
};

export const setActiveVaultId = (vaultId) => {
    const identity = readIdentity();
    if (!identity) {
        throw new Error('Аккаунт не найден');
    }
    if (!identity.vaults.some((vault) => vault.id === vaultId)) {
        throw new Error('Сейф не найден');
    }
    return writeIdentity({...identity, activeVaultId: vaultId});
};

/** Snapshot suitable for export / extension setup. */
export const getIdentityExportMeta = (identity = readIdentity()) => {
    if (!identity?.account?.id) return null;
    const vault = getActiveVault(identity);
    return {
        accountId: identity.account.id,
        accountStatus: identity.account.status || ACCOUNT_STATUS.UNCONFIRMED,
        vaultId: vault?.id || identity.account.id,
        vaultName: vault?.name || DEFAULT_VAULT_NAME,
    };
};
